import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonButton } from '@/components/ui/common-button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const AddMissingWagonReceiptDialog = ({ open, onOpenChange, trip, tripsList = [], onSelectTrip, submitting, onSubmit }) => {
  const [qty, setQty] = useState('');
  const [receivingDate, setReceivingDate] = useState('');
  const [selectedTripId, setSelectedTripId] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setQty('');
      const now = new Date();
      const tzoffset = now.getTimezoneOffset() * 60000;
      const localISOTime = (new Date(now - tzoffset)).toISOString().slice(0, 16);
      setReceivingDate(localISOTime);
      setSelectedTripId(trip?.id || (tripsList[0]?.id || ''));
      setErrors({});
    }
  }, [open, trip?.id, tripsList]);

  const activeTrip = trip || tripsList.find(t => t.id === selectedTripId) || tripsList[0];

  const validate = () => {
    const newErrors = {};
    if (!activeTrip && !selectedTripId) {
      newErrors.trip = 'Please select a Rake Movement';
    }

    if (!qty) {
      newErrors.qty = 'Quantity is required';
    } else {
      const parsedQty = parseFloat(qty);
      if (isNaN(parsedQty) || parsedQty <= 0) {
        newErrors.qty = 'Quantity must be a positive number';
      }
    }

    if (!receivingDate) {
      newErrors.receivingDate = 'Receiving date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const targetId = activeTrip?.id || selectedTripId;

    if (onSubmit) {
      await onSubmit({
        qty: parseFloat(qty),
        receiving_date: new Date(receivingDate).toISOString(),
        remarks: null
      }, targetId);
      setQty('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">
              Record New Missing Wagon Receipt
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {tripsList.length > 0 && !trip ? (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Select Rake Movement *</label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  value={selectedTripId}
                  onChange={(e) => {
                    setSelectedTripId(e.target.value);
                    const found = tripsList.find(t => t.id === e.target.value);
                    if (found && onSelectTrip) onSelectTrip(found);
                  }}
                >
                  {tripsList.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.trip_number || 'N/A'} (RR: {t.rr_number || 'N/A'})
                    </option>
                  ))}
                </select>
                {errors.trip && <span className="text-[11px] text-red-500">{errors.trip}</span>}
              </div>
            ) : (
              <div className="border border-border p-3 rounded-lg bg-card grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground block font-semibold">Trip Number</span>
                  <span className="font-semibold text-foreground">{activeTrip?.trip_number || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block font-semibold">RR Number</span>
                  <span className="font-semibold text-foreground">{activeTrip?.rr_number || 'N/A'}</span>
                </div>
              </div>
            )}

            <CommonInput
              label="Received Wagons"
              id="qty"
              type="number"
              step="0.01"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              error={errors.qty}
              required
              placeholder="e.g. 2"
            />

            <CommonInput
              label="Receiving Date"
              id="receivingDate"
              type="datetime-local"
              value={receivingDate}
              onChange={(e) => setReceivingDate(e.target.value)}
              error={errors.receivingDate}
              required
            />
          </div>

          <DialogFooter>
            <CommonButton type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </CommonButton>
            <CommonButton type="submit" loading={submitting}>
              Save Receipt
            </CommonButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMissingWagonReceiptDialog;
